/**
 * Modal Component
 *
 * GAP-014: Accessibility-compliant modal with proper focus trap
 * - Uses Radix UI Dialog for built-in focus management
 * - Traps focus within modal when open
 * - Returns focus to trigger when closed
 * - Supports Escape key to close (when closable)
 * - aria-modal and role="dialog" for screen readers
 */

import { type ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { XMarkIcon } from '@heroicons/react/24/outline';

import { cn } from '../../utils/cn';

export interface ModalProps {
  open?: boolean;
  isOpen?: boolean; // Alias for backwards compatibility
  onOpenChange?: (open: boolean) => void;
  onClose?: () => void; // Alias for backwards compatibility
  title?: string;
  description?: string;
  children: ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | 'full';
  /** Whether the modal can be closed by clicking outside or pressing Escape */
  closable?: boolean;
}

const sizeClasses = {
  sm: 'max-w-sm',       // 384px
  md: 'max-w-md',       // 448px
  lg: 'max-w-lg',       // 512px
  xl: 'max-w-xl',       // 576px
  '2xl': 'max-w-2xl',   // 672px
  '3xl': 'max-w-3xl',   // 768px
  '4xl': 'max-w-4xl',   // 896px
  full: 'max-w-[90vw]', // 90% viewport width
};

export function Modal({
  open,
  isOpen,
  onOpenChange,
  onClose,
  title,
  description,
  children,
  className,
  size = 'md',
  closable = true,
}: ModalProps) {
  const isModalOpen = open ?? isOpen ?? false;
  const handleOpenChange = onOpenChange ?? ((o: boolean) => !o && onClose?.());

  return (
    <Dialog.Root open={isModalOpen} onOpenChange={closable ? handleOpenChange : undefined}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-fade-in" />
        {/* GAP-014: Focus trap is handled by Radix Dialog automatically */}
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-full -translate-x-1/2 -translate-y-1/2',
            'rounded-lg bg-white p-6 shadow-dropdown',
            'data-[state=open]:animate-slide-up',
            // GAP-014: Visible focus ring for keyboard navigation
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-mv-blue-500 focus-visible:ring-offset-2',
            // Max height and overflow for tall content
            'max-h-[90vh] overflow-y-auto',
            sizeClasses[size],
            className
          )}
          aria-modal="true"
          onPointerDownOutside={closable ? undefined : (e) => e.preventDefault()}
          onEscapeKeyDown={closable ? undefined : (e) => e.preventDefault()}
        >
          {title && (
            <div className="flex items-center justify-between">
              <Dialog.Title className="text-lg font-semibold text-mv-gray-900">
                {title}
              </Dialog.Title>
              {closable && (
                <Dialog.Close asChild>
                  <button
                    className={cn(
                      // GAP-015: text-mv-gray-500 for WCAG AA contrast
                      'rounded p-1 text-mv-gray-500 hover:bg-mv-gray-100 hover:text-mv-gray-700',
                      // GAP-014: Visible focus ring for keyboard navigation
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-mv-blue-500'
                    )}
                    aria-label="Sluiten"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </Dialog.Close>
              )}
            </div>
          )}
          {description && (
            <Dialog.Description className="mt-2 text-sm text-mv-gray-500">
              {description}
            </Dialog.Description>
          )}
          <div className={cn(title && 'mt-4')}>{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
